import React, { useState } from 'react'
import { useMutation } from '@apollo/client'
import { useRouteMatch, useHistory } from 'react-router-dom'
import { useSnackbar, useSnackbarSuccess, useSnackbarError } from 'lib/snackbar'
import { get } from 'lodash'
import { makeStyles } from '@material-ui/styles'
import Box from '@material-ui/core/Box'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Avatar from '@material-ui/core/Avatar'
import CircularProgress from '@material-ui/core/CircularProgress'
import IconButton from '@material-ui/core/IconButton'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemText from '@material-ui/core/ListItemText'
import ActionsMenu from 'components/actions-menu'
import SendIcon from 'components/material-symbol/icons/Send'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import ChatBubble from 'components/material-symbol/icons/ChatBubble'
import DeleteStudentDialog from './delete-student-dialog'
import SessionDetailsDialog from 'components/session-details-dialog'
import { parseEvent } from 'utils/eventUtils'
import EventDetailsDialog from 'pages/pga-coach/bookings/events/event-details-dialog'
import EditOutlined from 'components/material-symbol/icons/Edit'
import CalendarAddOn from 'components/material-symbol/icons/CalendarAddOn'
import MoreHoriz from 'components/material-symbol/icons/MoreHoriz'
import { useIsTablet } from 'lib/hooks'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import { inviteToPlatformMutation } from './mutations'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import { gtmEvent } from 'lib/gtm'
import { useCoachBookStudentNavigation } from 'lib/pga-coach/use-coach-fast-book-student'
import { useAuth } from 'lib/auth'
import { isPgaLink } from 'lib/utils'
import { getInitials } from 'lib/text-display'
import { RefetchContext } from 'lib/utils/refetchContext'
import { useStudentListNameAgeStyles } from './student-list-name-age-styles'
import RetentionStatusChip from 'components/student-insights/retention-status-chip'

const useStyles = makeStyles(theme => ({
  actions: {
    textAlign: 'right',
    width: 1,
    whiteSpace: 'nowrap',
  },
  actionButtons: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
  },
  borderedActionButton: {
    padding: 6,
    width: 32,
    height: 32,
    borderRadius: 8,
    border: '1px solid #C4C7C9',
    backgroundColor: theme.palette.common.white,
    color: theme.palette.primary.main,
    transition: theme.transitions.create(
      ['background-color', 'border-color', 'color'],
      { duration: theme.transitions.duration.short },
    ),
    '&:hover': {
      backgroundColor: '#EBEBEB',
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  plainActionButton: {
    padding: 0,
    minWidth: 0,
    width: 24,
    height: 24,
    borderRadius: 4,
    color: '#6B7C87',
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.short,
    }),
    '&:hover': {
      backgroundColor: 'rgba(0, 35, 75, 0.04)',
      color: '#172024',
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  link: {
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  icon: {
    color: theme.palette.grey[600],
    fontSize: '1.25rem',
  },
  textSecondary: {
    color: theme.palette.text.secondary,
    fontSize: '12px',
    lineHeight: '16px',
  },
  sessionCell: {
    minWidth: 0,
  },
  sessionCellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  sessionDateBadge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
  },
  sessionDateMonth: {
    fontSize: '10px',
    fontWeight: 500,
    lineHeight: '12px',
    letterSpacing: '0.4px',
    color: '#6B7C87',
  },
  sessionDateDay: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '20px',
    color: '#172024',
  },
  sessionText: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
  },
  sessionTime: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '16px',
    color: '#6B7C87',
  },
  statusCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    minWidth: 0,
  },
  menuIcon: {
    minWidth: 36,
    color: 'rgba(0, 0, 0, 0.54)',
  },
}))

const StudentItemView = ({
  student,
  showInsights = false,
  refetchStudents,
  onReschedule,
  onPayment,
  onCancellation,
}) => {
  const { user } = useAuth()
  const { navigateToBookStudent } = useCoachBookStudentNavigation()
  const { path } = useRouteMatch()
  const classes = useStyles()
  const nameAgeClasses = useStudentListNameAgeStyles()
  const history = useHistory()

  const [isOpen, setIsOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const isTablet = useIsTablet()

  const [inviteToPlatform, { loading }] = useMutation(inviteToPlatformMutation)

  // ----> Handle details dialog.
  const hasSession = student?.upcomingSession.__typename !== null
  const type = student.upcomingSession.__typename

  const DetailsDialog = () => {
    if (!hasSession) return null

    return type === 'Session' ? (
      <SessionDetailsDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        booking={student?.upcomingSession}
        onReschedule={onReschedule}
        onPayment={onPayment}
        onCancellation={onCancellation}
      />
    ) : (
      <RefetchContext.Provider value={refetchStudents}>
        <EventDetailsDialog
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          event={parseEvent(student?.upcomingSession)}
          onPayment={onPayment}
        />
      </RefetchContext.Provider>
    )
  }

  // Only show the Book button for internal booking. Otherwise the button goes nowhere.
  const showBookButton =
    user.coach.bookingUrl !== null ? isPgaLink(user.coach.bookingUrl) : false

  const isEditButtonEnabled = !student.hasAccount

  const handleBookSessionClick = event => {
    event?.stopPropagation?.()
    gtmEvent({
      event: 'formSubmit',
      formCategory: 'book-session',
      formAction: 'clicked-book-session-button',
    })
    if (navigateToBookStudent(history, { studentId: student.id })) {
      return
    }
    const defaultPath = new URL(user.coach.bookingUrl).pathname
    window.open(
      `${defaultPath}?student=${student.id}`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  const handleInvitation = async () => {
    try {
      const { data } = await inviteToPlatform({
        variables: { studentIds: [student.id] },
      })
      const success = get(data, ['SendConnectionInvitation', 'success'])
      if (!success) return snackbarError()
      snackbarSuccess(`${student.firstName} invited to platform`)
    } catch (e) {
      snackbarError()
    }
  }

  const studentURL = `${path
    .split('/')
    .slice(0, 2)
    .join('/')}/student`
  const ageGroupLabel = student.isMinor ? 'Junior' : 'Adult'

  return (
    <>
      <TableRow
        className={nameAgeClasses.row}
        data-testid={`student-list-row-${student.id}`}
        onClick={() => history.push(`${studentURL}/${student.id}`)}
      >
        <TableCell className={nameAgeClasses.info}>
          <Box
            className={nameAgeClasses.nameCell}
            data-testid={`student-list-name-${student.id}`}
          >
            <Avatar className={nameAgeClasses.studentAvatar}>
              {getInitials(`${student.firstName} ${student.lastName}`)}
            </Avatar>
            <Box className={nameAgeClasses.info}>
              <Typography className={nameAgeClasses.text} noWrap>
                {student.firstName} {student.lastName}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        {showInsights && (
          <TableCell className={nameAgeClasses.info}>
            {student.insights ? (
              <Tooltip title={student.insights.reason}>
                <Box className={classes.statusCell} tabIndex={0}>
                  <RetentionStatusChip status={student.insights.status} />
                  {!isTablet && (
                    <Typography className={classes.textSecondary} noWrap>
                      {student.insights.reason}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            ) : (
              <Typography className={classes.textSecondary}>
                No lessons yet
              </Typography>
            )}
          </TableCell>
        )}
        <TableCell>
          <span
            className={`${nameAgeClasses.ageGroupChip} ${
              student.isMinor
                ? nameAgeClasses.ageGroupChipJunior
                : nameAgeClasses.ageGroupChipAdult
            }`}
          >
            {ageGroupLabel}
          </span>
        </TableCell>
        {!isTablet && (
          <TableCell className={classes.sessionCell}>
            {hasSession ? (
              <Box
                className={`${classes.sessionCellContent} ${classes.link}`}
                data-testid={`student-list-session-${student.id}`}
                onClick={e => {
                  e.stopPropagation()
                  setIsOpen(type === 'Session' ? !isOpen : 'detail')
                }}
              >
                <Box className={classes.sessionDateBadge} aria-hidden="true">
                  <Typography className={classes.sessionDateMonth}>
                    {student.upcomingSession.sessionMonth}
                  </Typography>
                  <Typography className={classes.sessionDateDay}>
                    {student.upcomingSession.sessionDay}
                  </Typography>
                </Box>
                <Box className={classes.sessionText}>
                  <Typography className={nameAgeClasses.text} noWrap>
                    {student.upcomingSession.title}
                  </Typography>
                  <Typography className={classes.sessionTime} noWrap>
                    {student.upcomingSession.sessionTime}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography className={classes.textSecondary}>
                No upcoming sessions
              </Typography>
            )}
          </TableCell>
        )}
        <TableCell
          className={classes.actions}
          onClick={e => e.stopPropagation()}
        >
          {loading ? (
            <CircularProgress color="primary" size={24} />
          ) : (
            <Box className={classes.actionButtons}>
              {showBookButton && (
                <Tooltip title="Book a session">
                  <IconButton
                    aria-label="Book a session"
                    data-testid={`student-list-book-session-${student.id}`}
                    className={classes.borderedActionButton}
                    onClick={handleBookSessionClick}
                  >
                    <CalendarAddOn fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <SensitiveInfoWrapper>
                <StreamSendMessageButton
                  userIds={[student.representatives[0]?.contact.externalId]}
                  component={props => (
                    <Tooltip title="Message student">
                      <IconButton
                        {...props}
                        aria-label="Message student"
                        data-testid={`student-list-message-${student.id}`}
                        className={classes.borderedActionButton}
                      >
                        <ChatBubble fontSize="small" fill={0} />
                      </IconButton>
                    </Tooltip>
                  )}
                  onClick={e => e.stopPropagation()}
                />
              </SensitiveInfoWrapper>
              <ActionsMenu
                ariaId="student-actions"
                aria-label="Student actions"
                data-testid={`student-list-actions-menu-${student.id}`}
                buttonContent={<MoreHoriz fontSize="small" />}
                className={classes.plainActionButton}
                color="default"
              >
                {isEditButtonEnabled && (
                  <MenuItem
                    onClick={() =>
                      history.push(`${studentURL}/${student.id}/edit`)
                    }
                  >
                    <ListItemIcon className={classes.menuIcon}>
                      <EditOutlined fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Edit Student Details" />
                  </MenuItem>
                )}
                {!student.hasAccount && (
                  <MenuItem onClick={handleInvitation}>
                    <ListItemIcon className={classes.menuIcon}>
                      <SendIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Invite to Platform" />
                  </MenuItem>
                )}
                <SensitiveInfoWrapper>
                  <MenuItem onClick={() => setIsDeleteDialogOpen(true)}>
                    <ListItemIcon className={classes.menuIcon}>
                      <DeleteOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary="Delete Student" />
                  </MenuItem>
                </SensitiveInfoWrapper>
              </ActionsMenu>
            </Box>
          )}
        </TableCell>
      </TableRow>
      <DeleteStudentDialog
        student={student}
        onSuccess={() => refetchStudents()}
        setOpen={setIsDeleteDialogOpen}
        open={isDeleteDialogOpen}
      />
      <DetailsDialog />
    </>
  )
}

export default StudentItemView
